if (!customElements.get('buying-options')) {
  customElements.define(
    'buying-options',
    class BuyingOptions extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.tiles = Array.from(this.querySelectorAll('[data-buying-option]'));
        this.prompt = this.querySelector('[data-buying-options-prompt]');
        this.mixedCaseBuilder = this.querySelector('[data-mixed-case-builder]');
        this.mixedCaseProgress = this.querySelector('[data-mixed-case-progress]');
        this.mixedCaseHelp = this.querySelector('[data-mixed-case-help]');
        this.mixedCaseVariants = Array.from(this.querySelectorAll('[data-mixed-case-variant]'));
        this.mixedCaseCounts = new Map();

        if (!this.productInfo || this.tiles.length === 0) return;

        this.quantityInput = this.productInfo.querySelector(
          '[data-buying-option-control="quantity"] input[name="quantity"]'
        );
        this.productFormElement = this.productInfo.querySelector('[data-buying-option-control="submit"] product-form');
        this.productForm = this.productFormElement?.querySelector('form');
        this.submitButton = this.productForm?.querySelector('[type="submit"][name="add"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.dynamicCheckout = this.productForm?.querySelector('.shopify-payment-button');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (this.submitButton) {
          this.originalSubmitText = this.submitButtonText?.textContent.trim() || 'Add to cart';
          this.standardSubmitDisabled = this.submitButton.disabled;
        }

        if (this.quantityInput && this.productForm) {
          this.quantityInput.removeAttribute('form');
          this.actualQuantityInput = document.createElement('input');
          this.actualQuantityInput.type = 'hidden';
          this.actualQuantityInput.name = 'quantity';
          this.productForm.appendChild(this.actualQuantityInput);
          this.quantityInput.addEventListener('change', () => this.handleQuantityChange());
          this.quantityInput.addEventListener('input', () => this.handleQuantityChange());
        }

        this.tiles.forEach((tile) => tile.addEventListener('click', () => this.selectOption(tile)));
        this.mixedCaseVariants.forEach((variant) => this.bindMixedCaseVariant(variant));
        this.productForm?.addEventListener('submit', (event) => this.handleMixedCaseSubmit(event), true);
        this.restorePendingOption();
      }

      selectOption(selectedTile) {
        const linkedProductId = selectedTile.dataset.buyingOptionProductId;
        const linkedProductUrl = selectedTile.dataset.buyingOptionProductUrl;

        if (
          linkedProductId &&
          linkedProductUrl &&
          linkedProductId !== this.productInfo.dataset.productId
        ) {
          this.swapLinkedProduct(selectedTile, linkedProductUrl);
          return;
        }

        const changedOption = this.selectedTile !== selectedTile;
        const nextMode = selectedTile.dataset.buyingOptionMode === 'mix_and_match' ? 'mix_and_match' : 'single_variant';
        if (nextMode === 'mix_and_match' && this.mode !== 'mix_and_match' && this.submitButton) {
          this.standardSubmitDisabled = this.submitButton.disabled;
          this.originalSubmitText = this.submitButtonText?.textContent.trim() || this.originalSubmitText;
        }
        this.selectedTile = selectedTile;
        this.tiles.forEach((tile) => tile.setAttribute('aria-pressed', String(tile === selectedTile)));

        this.multiplier = Math.max(1, Number.parseInt(selectedTile.dataset.buyingOptionMultiplier, 10) || 1);
        this.mode = nextMode;
        if (this.mode === 'mix_and_match' && this.mixedCaseVariants.length === 0) this.mode = 'single_variant';

        this.productInfo.dataset.buyingOptionsState = 'selected';
        this.productInfo.dataset.buyingOptionsMode = this.mode;

        if (changedOption) this.resetMixedCase();
        this.updateActualQuantity();
        this.updatePurchaseControls();

        const label = selectedTile.dataset.buyingOptionLabel;
        if (this.prompt) {
          this.prompt.textContent =
            this.mode === 'mix_and_match'
              ? `${label} selected — build a complete box below.`
              : `${label} selected — ${this.multiplier} ${this.multiplier === 1 ? 'item' : 'items'} per quantity selected.`;
        }

        const firstControl =
          this.mode === 'mix_and_match'
            ? this.mixedCaseBuilder
            : this.productInfo.querySelector('[data-buying-option-control="variants"]') ||
              this.productInfo.querySelector('[data-buying-option-control="quantity"]');
        firstControl?.scrollIntoView({ block: 'nearest' });
      }

      swapLinkedProduct(selectedTile, productUrl) {
        this.rememberPendingOption(selectedTile);

        if (
          typeof this.productInfo.renderProductInfo !== 'function' ||
          typeof this.productInfo.buildRequestUrlWithParams !== 'function' ||
          typeof this.productInfo.handleSwapProduct !== 'function'
        ) {
          window.location.assign(productUrl);
          return;
        }

        this.tiles.forEach((tile) => {
          tile.disabled = true;
          tile.setAttribute('aria-pressed', String(tile === selectedTile));
        });
        selectedTile.classList.add('loading');
        this.productInfo.setAttribute('aria-busy', 'true');

        this.productInfo.renderProductInfo({
          requestUrl: this.productInfo.buildRequestUrlWithParams(productUrl, [], true),
          targetId: selectedTile.id,
          callback: this.productInfo.handleSwapProduct(productUrl, true),
        });
      }

      rememberPendingOption(selectedTile) {
        try {
          window.sessionStorage.setItem(
            'lion-pending-buying-option',
            JSON.stringify({
              optionKey: selectedTile.dataset.buyingOptionKey,
              productId: selectedTile.dataset.buyingOptionProductId,
              createdAt: Date.now(),
            })
          );
        } catch (_error) {
          // Product switching still works when session storage is unavailable.
        }
      }

      restorePendingOption() {
        try {
          const storedValue = window.sessionStorage.getItem('lion-pending-buying-option');
          if (!storedValue) return;

          const pendingOption = JSON.parse(storedValue);
          const isCurrentProduct = pendingOption.productId === this.productInfo.dataset.productId;
          const isRecent = Date.now() - pendingOption.createdAt < 30000;
          if (!isRecent) {
            window.sessionStorage.removeItem('lion-pending-buying-option');
            return;
          }
          if (!isCurrentProduct) return;

          window.sessionStorage.removeItem('lion-pending-buying-option');
          const matchingTile = this.tiles.find(
            (tile) => tile.dataset.buyingOptionKey === pendingOption.optionKey
          );
          if (matchingTile) queueMicrotask(() => this.selectOption(matchingTile));
        } catch (_error) {
          // Leave the normal unselected state in place if storage is unavailable.
        }
      }

      bindMixedCaseVariant(variant) {
        const variantId = variant.dataset.variantId;
        const decrease = variant.querySelector('[data-mixed-case-decrease]');
        const increase = variant.querySelector('[data-mixed-case-increase]');

        this.mixedCaseCounts.set(variantId, 0);
        decrease?.addEventListener('click', () => this.changeMixedCaseCount(variant, -1));
        increase?.addEventListener('click', () => this.changeMixedCaseCount(variant, 1));
      }

      changeMixedCaseCount(variant, delta) {
        if (this.mode !== 'mix_and_match') return;

        const variantId = variant.dataset.variantId;
        const current = this.mixedCaseCounts.get(variantId) || 0;
        const selectedTotal = this.getMixedCaseSelectedTotal();
        const target = this.getMixedCaseTarget();
        if (delta > 0 && selectedTotal >= target) return;

        this.mixedCaseCounts.set(variantId, Math.max(0, current + delta));
        this.updateMixedCaseBuilder();
      }

      handleQuantityChange() {
        this.updateActualQuantity();
        this.updateMixedCaseBuilder();
      }

      updateActualQuantity() {
        if (!this.actualQuantityInput || !this.quantityInput || !this.multiplier) return;

        const count = Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
        this.actualQuantityInput.value = count * this.multiplier;
      }

      getCaseCount() {
        return Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);
      }

      getMixedCaseTarget() {
        return Math.max(1, this.multiplier || 1) * this.getCaseCount();
      }

      getMixedCaseSelectedTotal() {
        return Array.from(this.mixedCaseCounts.values()).reduce((total, count) => total + count, 0);
      }

      updateMixedCaseBuilder() {
        if (!this.mixedCaseBuilder) return;

        const selected = this.getMixedCaseSelectedTotal();
        const target = this.getMixedCaseTarget();
        const remaining = target - selected;

        this.mixedCaseVariants.forEach((variant) => {
          const variantId = variant.dataset.variantId;
          const count = this.mixedCaseCounts.get(variantId) || 0;
          const output = variant.querySelector('[data-mixed-case-count]');
          const decrease = variant.querySelector('[data-mixed-case-decrease]');
          const increase = variant.querySelector('[data-mixed-case-increase]');
          const available = variant.dataset.variantAvailable === 'true';

          if (output) output.textContent = count;
          if (decrease) decrease.disabled = count === 0;
          if (increase) increase.disabled = !available || selected >= target;
          variant.classList.toggle('has-selection', count > 0);
        });

        if (this.mixedCaseProgress) {
          this.mixedCaseProgress.textContent =
            selected === target ? `${selected} of ${target} selected — case complete` : `${selected} of ${target} selected`;
        }

        if (this.mixedCaseHelp) {
          if (remaining > 0) {
            this.mixedCaseHelp.textContent = `Choose ${remaining} more ${remaining === 1 ? 'item' : 'items'} to complete your case.`;
          } else if (remaining < 0) {
            this.mixedCaseHelp.textContent = `Remove ${Math.abs(remaining)} ${Math.abs(remaining) === 1 ? 'item' : 'items'} to complete your case.`;
          } else {
            this.mixedCaseHelp.textContent = 'Your case is complete and ready to add to cart.';
          }
        }

        if (this.mode === 'mix_and_match') this.setMixedCaseSubmitState(selected !== target);
      }

      updatePurchaseControls() {
        const isMixed = this.mode === 'mix_and_match';
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = isMixed;

        if (isMixed) {
          this.updateMixedCaseBuilder();
        } else {
          this.restoreStandardSubmitState();
        }
      }

      setMixedCaseSubmitState(disabled) {
        if (!this.submitButton) return;
        this.submitButton.disabled = disabled;
        this.submitButton.setAttribute('aria-disabled', String(disabled));
        if (this.submitButtonText) {
          this.submitButtonText.textContent = disabled ? 'Complete your case' : 'Add completed case to cart';
        }
      }

      restoreStandardSubmitState() {
        if (!this.submitButton) return;
        this.submitButton.disabled = Boolean(this.standardSubmitDisabled);
        this.submitButton.removeAttribute('aria-disabled');
        if (this.submitButtonText) this.submitButtonText.textContent = this.originalSubmitText;
      }

      resetMixedCase() {
        this.mixedCaseCounts.forEach((_count, variantId) => this.mixedCaseCounts.set(variantId, 0));
        this.updateMixedCaseBuilder();
      }

      async handleMixedCaseSubmit(event) {
        if (this.mode !== 'mix_and_match') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const target = this.getMixedCaseTarget();
        if (this.getMixedCaseSelectedTotal() !== target) {
          this.updateMixedCaseBuilder();
          this.mixedCaseBuilder?.scrollIntoView({ block: 'nearest' });
          return;
        }

        const items = this.mixedCaseVariants
          .map((variant) => ({
            id: Number.parseInt(variant.dataset.variantId, 10),
            quantity: this.mixedCaseCounts.get(variant.dataset.variantId) || 0,
            properties: {
              '_Buying option': this.selectedTile?.dataset.buyingOptionLabel || 'Build a case',
              '_Case size': String(this.multiplier),
              '_Cases': String(this.getCaseCount()),
            },
          }))
          .filter((item) => item.quantity > 0);

        this.setLoading(true);
        this.productFormElement?.handleErrorMessage?.();

        try {
          const body = { items };
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this case to the cart.');
          }

          if (this.cart) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'buying-options',
              cartData: result,
            });
            this.cart.renderContents(result);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          } else {
            window.location = window.routes.cart_url;
            return;
          }

          this.resetMixedCase();
        } catch (error) {
          this.productFormElement?.handleErrorMessage?.(error.message);
        } finally {
          this.setLoading(false);
          this.updateMixedCaseBuilder();
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.setAttribute('aria-disabled', String(isLoading));
        const spinner = this.submitButton.querySelector('.loading__spinner');
        spinner?.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
